import { useFloatingPanel } from '@/components/admin/protocoles/step/floating-panel';
import { latestPlays, newestFirst, pinIsBehind, pinWord } from '@/lib/references';
import { useReferenceVersions } from '@/lib/use-reference-versions';
import { versionedDay } from '@/lib/versioned-labels';
import type { VersionPin, VersionSummary, VersionedKind } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

/**
 * The control of a reference (vitrine.md §3.1, V3, V16; planche
 * `references.html`, variante A retenue le 2026-10-14): ONE pill-menu that says
 * which version of an object a content plays - `latest`, the default, or a
 * precise `vN` - and, under it, the signal that a pinned reference is behind.
 *
 * It is the same control everywhere a content cites another object: the
 * criterion of an objective, the protocol of a page, the persona, the texts of
 * the noyaux. Written once, because five spellings of « quelle version » would
 * be five menus that disagree about the order of the versions or the word of
 * the default.
 *
 * ## The pill
 *
 * « latest » with the version it plays - « latest · v6 sur le dev » (revue du
 * 2026-10-14) - or « v4 » when pinned. Its menu opens « latest (suit la
 * dernière) » first, then every version from the most recent to the oldest,
 * with the marks the pointers give them - « latest dev » on the version the
 * development pointer sits on, « latest prod » on the production one.
 *
 * The versions are read when the menu OPENS, never at mount (`useReferenceVersions`):
 * ten cited criteria would otherwise be ten requests as the editor opens. The
 * pill itself needs only the development number, which the card the caller
 * already holds carries.
 *
 * ## The signal
 *
 * « Sens profond v6 disponible - ce protocole épingle v4 » and « Mettre à jour »,
 * only for a PINNED reference that is behind the latest dev version, and only
 * when the editor can write (§3.3). The click moves the pin onto the latest dev
 * version and changes the DRAFT, nothing else - the editor's own autosave and
 * undo carry it from there - and the line then says what it did, « Brouillon
 * modifié : v4 -> v6 ».
 *
 * ## The menu is a radiogroup, not a dialog
 *
 * What an operator does in it is pick and leave, at the régime of the version
 * chooser it replaces (`criterion-requirement-row`, issue #104): `fixed`, closed
 * by Escape and by a click outside (`useFloatingPanel`), and never portalled.
 */

/** The maquette's `340`, as a number: a placement needs a width before the DOM has one. */
const MENU_WIDTH = 340;

const PILL_SHAPE =
  'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-0.5 font-semibold text-caption leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-default';

/** What the pill and the signal both need to name the reference. */
export interface ReferenceSubject {
  /** What the reference points at: its kind decides which detail the menu reads. */
  readonly kind: VersionedKind;
  readonly id: string;
  /** « Sens profond » - the object's name, as the menu and the signal say it. */
  readonly name: string;
  /** « ce protocole », « cette expérience » - who cites it, for the signal and the menu. */
  readonly who: string;
  readonly pin: VersionPin;
  /**
   * The number of the version the development pointer sits on, read off the card
   * the caller holds, or null while the library is read or when the object has
   * none yet (the texts of the noyaux before their first version).
   */
  readonly developmentNumber: number | null;
}

/**
 * The PILL-MENU alone: « latest · v6 sur le dev », or « v4 », and the menu it
 * opens. A prefix for the `data-testid`s: `reference` gives `reference-menu`.
 */
export function ReferencePill({
  subject,
  readOnly,
  testId = 'reference',
  menuTestId,
  onPin,
}: {
  readonly subject: ReferenceSubject;
  readonly readOnly: boolean;
  readonly testId?: string;
  /** The pill's own `data-testid`, for a screen whose tests and captures already name it. */
  readonly menuTestId?: string;
  /** The new pin, a menu choice. The caller writes the DRAFT. */
  readonly onPin: (pin: VersionPin) => void;
}) {
  const { kind, id, name, who, pin, developmentNumber } = subject;
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        <button
          ref={setAnchor}
          type="button"
          data-testid={menuTestId ?? `${testId}-menu`}
          data-pin={String(pin)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Version de « ${name} » jouée par ${who}`}
          disabled={readOnly}
          onClick={() => setOpen((was) => !was)}
          className={`${PILL_SHAPE} ${
            pin === 'latest'
              ? 'border border-bleu-nuit-tint bg-bleu-nuit-tint text-bleu-nuit'
              : 'border border-gray-300 bg-gray-100 text-gray-900'
          }`}
        >
          {pinWord(pin)}
          {readOnly ? null : <Icon name="expand_more" size={14} />}
        </button>
        {pin === 'latest' && developmentNumber !== null ? (
          <span data-testid={`${testId}-joue`} className="text-caption text-gray-600">
            {latestPlays(developmentNumber)}
          </span>
        ) : null}
      </span>

      {/* The anchor is a MOUNT condition, not a prop that may be null: see the
          note in `criterion-requirement-row` - a panel without its anchor falls
          into the regime where nothing closes it. */}
      {open && anchor !== null ? (
        <ReferenceMenu
          kind={kind}
          id={id}
          name={name}
          who={who}
          pin={pin}
          anchor={anchor}
          testId={testId}
          onClose={close}
          onPick={(next) => {
            setOpen(false);
            onPin(next);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The SIGNAL: « Sens profond v6 disponible - ce protocole épingle v4 » and
 * « Mettre à jour », for a PINNED reference that is behind - and, once the click
 * landed, « Brouillon modifié : v4 → v6 ». Nothing for `latest`, which follows,
 * and nothing in a read-only editor (§3.3).
 */
export function ReferenceSignal({
  subject,
  readOnly,
  movedFrom,
  testId = 'reference',
  onUpdate,
}: {
  readonly subject: ReferenceSubject;
  readonly readOnly: boolean;
  /** The pin the last « Mettre à jour » moved FROM, or null: said once, until the pin moves on. */
  readonly movedFrom: number | null;
  readonly testId?: string;
  /** The click: the pin moves onto the latest dev version. The caller writes the DRAFT. */
  readonly onUpdate: (to: number) => void;
}) {
  const { name, who, pin, developmentNumber } = subject;
  if (readOnly || developmentNumber === null || typeof pin !== 'number') return null;

  if (pinIsBehind(pin, developmentNumber)) {
    return (
      <span
        data-testid={`${testId}-signal`}
        className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-caption leading-[18px]"
      >
        <span className="rounded-md bg-bleu-ciel-tint px-2 py-px font-semibold text-bleu-ciel-deep">
          {name} v{developmentNumber} disponible - {who} épingle v{pin}
        </span>
        <button
          type="button"
          data-testid={`${testId}-maj`}
          onClick={() => onUpdate(developmentNumber)}
          className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          Mettre à jour
        </button>
      </span>
    );
  }
  if (movedFrom !== null && pin === developmentNumber) {
    return (
      <span data-testid={`${testId}-modifie`} className="text-caption text-vert-deep">
        Brouillon modifié : v{movedFrom} → v{pin}.
      </span>
    );
  }
  return null;
}

/**
 * The two together, in a column: for the places that give a reference a block of
 * its own (the persona, the protocol of a page, the texts of the noyaux), where
 * the signal sits right under the pill. The row of a criterion places the two
 * itself, because its pill shares a line with the threshold.
 */
export function ReferenceControl({
  subject,
  readOnly,
  testId = 'reference',
  onPin,
}: {
  readonly subject: ReferenceSubject;
  readonly readOnly: boolean;
  readonly testId?: string;
  readonly onPin: (pin: VersionPin) => void;
}) {
  const [movedFrom, setMovedFrom] = useState<number | null>(null);
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={`${testId}-control`}>
      <ReferencePill
        subject={subject}
        readOnly={readOnly}
        testId={testId}
        onPin={(pin) => {
          setMovedFrom(null);
          onPin(pin);
        }}
      />
      <ReferenceSignal
        subject={subject}
        readOnly={readOnly}
        movedFrom={movedFrom}
        testId={testId}
        onUpdate={(to) => {
          if (typeof subject.pin === 'number') setMovedFrom(subject.pin);
          onPin(to);
        }}
      />
    </div>
  );
}

/**
 * The menu: « latest (suit la dernière) » first, then the versions, newest
 * first, with the marks of the two pointers.
 */
function ReferenceMenu({
  kind,
  id,
  name,
  who,
  pin,
  anchor,
  testId,
  onClose,
  onPick,
}: {
  kind: VersionedKind;
  id: string;
  name: string;
  who: string;
  pin: VersionPin;
  anchor: HTMLElement;
  testId: string;
  onClose: () => void;
  onPick: (pin: VersionPin) => void;
}) {
  const { panelRef, at } = useFloatingPanel({ anchor, width: MENU_WIDTH, onClose });
  const read = useReferenceVersions(kind, id, true);
  const developmentNumber = read?.development.number ?? null;
  const productionNumber = read?.production?.number ?? null;

  // The versions the read lists; a pin it does not list (not read yet, or a
  // version the object no longer has) stays visible and checked, so the menu
  // never shows « latest » as chosen over a reference that is not.
  const listed = newestFirst(read?.versions ?? []);
  const known = listed.some((version) => version.number === pin);

  return (
    <div
      ref={panelRef}
      role="radiogroup"
      data-testid={`${testId}-options`}
      aria-label={`Version de « ${name} » jouée par ${who}`}
      style={{ left: at.left, top: at.top, width: MENU_WIDTH }}
      className="fixed z-30 max-h-96 max-w-[calc(100vw-16px)] overflow-y-auto rounded-md border-2 border-gray-200 bg-white p-1.5 text-left shadow-md"
    >
      <p className="px-2 pt-1 pb-1.5 text-caption text-gray-600 leading-4">
        Quelle version de « {name} » {who} joue
      </p>
      <Option
        testId={`${testId}-option-latest`}
        checked={pin === 'latest'}
        heading={
          <>
            <b>latest</b> <span className="font-normal text-gray-600">(suit la dernière)</span>
          </>
        }
        description={`Le défaut.${
          developmentNumber === null ? '' : ` Sur le dev : v${developmentNumber} aujourd’hui.`
        }${
          productionNumber === null ? '' : ` Sur la prod : v${productionNumber}.`
        } Suit chaque nouvelle version, sans geste.`}
        onPick={() => onPick('latest')}
      />
      <p className="mt-1 border-gray-100 border-t px-2 pt-2 pb-1 font-bold text-[11px] text-gray-500 uppercase tracking-wide">
        ou épingler une version précise
      </p>
      {typeof pin === 'number' && !known ? (
        <Option
          testId={`${testId}-option-${pin}`}
          checked
          heading={<b>v{pin}</b>}
          description="Épinglée sur cette version."
          onPick={() => onPick(pin)}
        />
      ) : null}
      {listed.map((version) => (
        <VersionOption
          key={version.id}
          version={version}
          pin={pin}
          developmentNumber={developmentNumber}
          productionNumber={productionNumber}
          testId={testId}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

function VersionOption({
  version,
  pin,
  developmentNumber,
  productionNumber,
  testId,
  onPick,
}: {
  version: VersionSummary;
  pin: VersionPin;
  developmentNumber: number | null;
  productionNumber: number | null;
  testId: string;
  onPick: (pin: VersionPin) => void;
}) {
  const marks: ReactNode[] = [];
  if (version.number === developmentNumber) {
    marks.push(
      <span
        key="dev"
        className="ml-1 rounded-sm bg-bleu-ciel-tint px-1.5 font-bold text-[11px] text-bleu-ciel-deep"
      >
        latest dev
      </span>,
    );
  }
  if (version.number === productionNumber) {
    marks.push(
      <span
        key="prod"
        className="ml-1 rounded-sm border border-vert-deep px-1.5 font-bold text-[11px] text-vert-deep"
      >
        latest prod
      </span>,
    );
  }
  return (
    <Option
      testId={`${testId}-option-${version.number}`}
      checked={pin === version.number}
      heading={
        <>
          <b>v{version.number}</b>
          {marks}
        </>
      }
      description={`${versionedDay(version.createdAt)}${version.note === null || version.note === '' ? '' : ` · ${version.note}`}`}
      onPick={() => onPick(version.number)}
    />
  );
}

function Option({
  testId,
  checked,
  heading,
  description,
  onPick,
}: {
  testId: string;
  checked: boolean;
  heading: ReactNode;
  description: string;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      // A `<button>` with `role="radio"` and not an `<input type="radio">`: the
      // option is a paragraph with marks in it, and a label wrapping a real radio
      // around that block would announce the whole paragraph as the radio's name.
      // biome-ignore lint/a11y/useSemanticElements: see above
      role="radio"
      data-testid={testId}
      aria-checked={checked}
      onClick={onPick}
      className={`flex w-full cursor-pointer items-start gap-2.5 rounded-sm px-2 py-1.5 text-left text-caption hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit ${
        checked ? 'bg-bleu-nuit-tint' : 'bg-white'
      }`}
    >
      <span
        aria-hidden="true"
        className={`mt-1 size-3.5 shrink-0 rounded-full border-2 ${
          checked ? 'border-4 border-bleu-nuit' : 'border-gray-400'
        }`}
      />
      <span className="min-w-0">
        <span className="block">{heading}</span>
        {description === '' ? null : (
          <span className="block text-caption text-gray-600 leading-4">{description}</span>
        )}
      </span>
    </button>
  );
}
